import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { type LucideIcon, MapPin, Layers, Users, Upload, MessageSquare, CheckCircle, ArrowRight, Sun, Moon, X, Check, Bell, Sparkles, History, Shield, Smartphone, Clock, AtSign, Columns2, Filter, Infinity, FolderOpen, KeyRound, Mail, SlidersHorizontal, Settings, Zap, CheckCheck, ImageIcon, Pin, Eye, ScrollText, ClipboardList, Ban, Lock } from "lucide-react";
import { useTheme } from "@/components/ui/theme-provider";

import logoBlack from "@assets/commentt_ai_logo_black.svg";
import lightViewer from "@assets/Screenshot_2026-02-23_at_3.44.29_PM_1771890376536.png";
import darkViewer from "@assets/Screenshot_2026-02-23_at_3.44.46_PM_1771890376535.png";
import lightComment from "@assets/Screenshot_2026-02-23_at_3.45.52_PM_1771890376535.png";
import darkComment from "@assets/Screenshot_2026-02-23_at_3.45.38_PM_1771890376535.png";

import phoneImg1 from "@assets/Screenshot_2026-02-25_at_1.59.29_PM_1772056771545.png";
import phoneImg2 from "@assets/Screenshot_2026-02-25_at_1.59.51_PM_1772056792946.png";
import phoneImg3 from "@assets/Screenshot_2026-02-25_at_2.00.45_PM_1772056847447.png";
import phoneImg4 from "@assets/Screenshot_2026-02-25_at_2.01.07_PM_1772056869404.png";

const phoneImages = [phoneImg1, phoneImg2, phoneImg3, phoneImg4];

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const elements = el.querySelectorAll('.landing-animate');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('landing-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return ref;
}


type AccentColor = "indigo" | "rose" | "sky" | "emerald" | "violet" | "amber" | "slate";

function PhoneFrame({ accent = "indigo", image }: { accent?: AccentColor; image?: string }) {
  const glowColor: Record<AccentColor, string> = {
    indigo:  "rgba(99,102,241,0.30)",
    rose:    "rgba(244,63,94,0.30)",
    sky:     "rgba(14,165,233,0.30)",
    emerald: "rgba(16,185,129,0.30)",
    violet:  "rgba(139,92,246,0.30)",
    amber:   "rgba(245,158,11,0.30)",
    slate:   "rgba(148,163,184,0.20)",
  };

  return (
    <div className="w-full flex justify-center py-8">
      {/* Fixed-size phone — 260×563px (9:19.5 iPhone ratio) */}
      <div className="relative" style={{ width: 338, aspectRatio: "9/19.5", flexShrink: 0 }}>

        {/* Volume up */}
        <div style={{
          position: "absolute", left: -5, top: 156,
          width: 5, height: 49,
          background: "#c8c8ce",
          borderRadius: "2px 0 0 2px",
        }} />
        {/* Volume down */}
        <div style={{
          position: "absolute", left: -5, top: 221,
          width: 5, height: 49,
          background: "#c8c8ce",
          borderRadius: "2px 0 0 2px",
        }} />
        {/* Power */}
        <div style={{
          position: "absolute", right: -5, top: 189,
          width: 5, height: 72,
          background: "#c8c8ce",
          borderRadius: "0 2px 2px 0",
        }} />

        {/* Phone body */}
        <div style={{
          position: "absolute", inset: 0,
          borderRadius: 57,
          background: "linear-gradient(145deg, #f8f8f8, #e0e0e6)",
          border: "2px solid #d0d0d5",
          boxShadow: "inset 0 0 4px rgba(0,0,0,0.12), 0 0 0 1px #a8a8b0, 0 20px 50px rgba(0,0,0,0.18)",
          padding: 7,
        }}>
          {/* Screen */}
          <div style={{
            width: "100%", height: "100%",
            borderRadius: 47,
            background: "#ffffff",
            border: "1px solid rgba(0,0,0,0.06)",
            overflow: "hidden",
            position: "relative",
          }}>
            {/* Screenshot */}
            {image && (
              <img
                src={image}
                alt=""
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            )}
            {/* Home indicator */}
            <div style={{
              position: "absolute",
              bottom: 10, left: "50%",
              transform: "translateX(-50%)",
              width: 114, height: 6,
              background: "rgba(0,0,0,0.18)",
              borderRadius: 10,
            }} />
          </div>
        </div>

      </div>
    </div>
  );
}

type FeatureBadge = { icon: LucideIcon; label: string };

const featureRows = [
  {
    icon: MapPin,
    title: "Pin, Draw, and Mark Up Exactly What You Mean",
    desc: "Click anywhere on a page to drop a numbered pin. Draw boxes, arrows, or freehand sketches to mark up exactly what you mean. Threaded comments, @mentions, and open/in-progress/resolved status keep every piece of feedback organized.",
    badges: [
      { icon: Smartphone, label: "Works on mobile" },
      { icon: Clock, label: "Attributed & timestamped" },
      { icon: MessageSquare, label: "Threaded replies" },
      { icon: AtSign, label: "@mentions" },
    ] as FeatureBadge[],
    accent: "indigo" as AccentColor,
    label: "Pinpoint annotations",
  },
  {
    icon: Layers,
    title: "See What Changed Between Versions",
    desc: "Upload a new version and generate a visual diff. Changed areas are highlighted page by page — so reviewers can go straight to what matters instead of hunting through a 50-page deck.",
    badges: [
      { icon: Columns2, label: "Side-by-side slider" },
      { icon: Filter, label: "Filter significant changes" },
    ] as FeatureBadge[],
    accent: "rose" as AccentColor,
    label: "Version diff heatmap",
  },
  {
    icon: Users,
    title: "Unlimited Guests, No Account Needed",
    desc: "Clients and execs join with just a name and email. No accounts, no passwords, no app downloads. Comments are fully attributed to them for the entire review.",
    badges: [
      { icon: Infinity, label: "No user caps" },
      { icon: FolderOpen, label: "No project limits" },
      { icon: KeyRound, label: "No passwords" },
    ] as FeatureBadge[],
    accent: "sky" as AccentColor,
    label: "Guest access",
  },
  {
    icon: Bell,
    title: "Stay in the Loop, On Your Terms",
    desc: "Set exactly which events trigger email alerts — new comments, @mentions, version uploads, version diff ready. Org admins set the baseline; every reviewer tunes their own preferences per document.",
    badges: [
      { icon: Mail, label: "Deep-linked emails" },
      { icon: SlidersHorizontal, label: "Per-document settings" },
      { icon: Settings, label: "Per-user preferences" },
    ] as FeatureBadge[],
    accent: "emerald" as AccentColor,
    label: "Notification control",
  },
  {
    icon: Sparkles,
    title: "Turn Vague Notes into Actionable Feedback",
    desc: "Hit Improve on any comment. The AI reads your note alongside the page image and the exact pin location, then rewrites it into a specific, design-ready instruction. You review the suggestion and accept or reject it.",
    badges: [
      { icon: Zap, label: "No prompting needed" },
      { icon: CheckCheck, label: "Accept or reject" },
      { icon: ImageIcon, label: "Sees the page image" },
    ] as FeatureBadge[],
    accent: "violet" as AccentColor,
    label: "AI comment improvement",
  },
  {
    icon: History,
    title: "Full Version Control for Every Upload",
    desc: "Every PDF upload is a tracked version. Promote, hide, or remove versions without losing any comments or history. Reviewers always see the current version — you control what's current.",
    badges: [
      { icon: Pin, label: "Version-locked comments" },
      { icon: Eye, label: "Hide or promote versions" },
      { icon: ScrollText, label: "Full history preserved" },
    ] as FeatureBadge[],
    accent: "amber" as AccentColor,
    label: "Version management",
  },
  {
    icon: Shield,
    title: "Comments That Stay Honest",
    desc: "Every comment is attributed, timestamped, and status-tracked across all collaborators — guests included. Empty pins can't linger. Commenting can be restricted per version so reviewers only annotate what's current.",
    badges: [
      { icon: ClipboardList, label: "Full audit trail" },
      { icon: Ban, label: "No ghost pins" },
      { icon: Lock, label: "Version-restricted commenting" },
    ] as FeatureBadge[],
    accent: "slate" as AccentColor,
    label: "Comment integrity",
  },
];

const competitors = [
  { tool: "Ziflow", pain: "$199/month + complex interface with steep learning curve" },
  { tool: "GoVisually", pain: "$16/user + 15 project cap on Lite plan" },
  { tool: "Google Slides", pain: "No version diffs, no pin annotations, no PDF support" },
];

const steps = [
  { step: "01", icon: Upload, title: "Upload PDF", desc: "Auto-converted to high-quality images, ready to annotate on any device." },
  { step: "02", icon: MessageSquare, title: "Pin Feedback", desc: "Drop pins, draw markups, @mention your team. Threaded comments keep everything organized." },
  { step: "03", icon: CheckCircle, title: "Resolve", desc: "Track status per comment. Generate a version diff to see exactly what changed." },
];

const pricingPlans = [
  {
    plan: "Free",
    price: "$0",
    period: "forever",
    features: ["1 PDF", "5 guests", "Pin annotations", "Version history"],
    cta: "Get Started",
    href: "/signup",
    highlight: false,
  },
  {
    plan: "Pro",
    price: "$29",
    period: "/month",
    features: ["Unlimited PDFs", "Unlimited guests", "Visual version diffs", "AI comment improvement", "@mentions & notifications"],
    cta: "Try Pro",
    href: "/signup",
    highlight: true,
  },
  {
    plan: "Team",
    price: "$79",
    period: "/month",
    features: ["Everything in Pro", "Multi-tenant orgs", "Custom branding", "Priority support"],
    cta: "Contact Sales",
    href: "/login",
    highlight: false,
  },
];

const comparisonRows = [
  { feature: "PDF Version Diff", commentt: true, ziflow: false, govisually: false },
  { feature: "Mobile-First UX", commentt: true, ziflow: false, govisually: true },
  { feature: "Flat-rate pricing", commentt: true, ziflow: false, govisually: false },
  { feature: "Unlimited Guests", commentt: true, ziflow: "partial" as const, govisually: true },
  { feature: "Unlimited Projects", commentt: true, ziflow: true, govisually: false },
  { feature: "Pin Annotations", commentt: true, ziflow: true, govisually: true },
  { feature: "AI Comment Assist", commentt: true, ziflow: false, govisually: false },
];

export default function Landing() {
  const { theme, toggleTheme } = useTheme();
  const scrollRef = useScrollReveal();

  return (
    <div className="min-h-screen bg-background" ref={scrollRef}>
      <header className="border-b border-border bg-background sticky top-0 z-50">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <img
              src={logoBlack}
              alt="Commentt.ai"
              className="h-3.5 dark:invert"
            />
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <Button asChild size="sm" variant="outline" className="h-9 text-sm font-medium px-4">
                <a href="/login">Sign In</a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="landing-hero-bg relative overflow-hidden">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 lg:pt-36 pb-0">
            <div className="text-center max-w-4xl mx-auto">
              <p className="landing-animate landing-visible text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-8">
                PDF Review &amp; Annotation
              </p>
              <h1 className="landing-animate landing-visible landing-animate-delay-1 font-display text-6xl sm:text-7xl lg:text-8xl font-extrabold leading-[1.0] tracking-tight mb-8">
                <span className="text-foreground">Design reviews</span>
                <br />
                <span className="landing-hero-gradient">that actually</span>
                <br />
                <span className="text-foreground">work on mobile.</span>
              </h1>
              <p className="landing-animate landing-visible landing-animate-delay-2 text-xl sm:text-2xl text-muted-foreground font-light leading-relaxed mb-10 max-w-2xl mx-auto">
                Built for the way clients actually review: on their phone, in 30 seconds, without signing up for yet another tool.
              </p>
              <div className="landing-animate landing-visible landing-animate-delay-3 flex items-center justify-center gap-4 mb-16 sm:mb-20">
                <Button asChild size="lg" className="h-12 px-10 text-base font-semibold landing-cta-glow rounded-xl">
                  <a href="/signup">
                    Start Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
              </div>
            </div>

            <div className="landing-animate landing-visible landing-animate-delay-5 relative max-w-5xl mx-auto">
              <div className="rounded-t-2xl border border-b-0 border-border bg-card overflow-hidden shadow-2xl shadow-black/10 dark:shadow-black/30">
                <div className="flex items-center gap-1.5 px-4 py-3 border-b border-border bg-muted/40">
                  <div className="w-3 h-3 rounded-full bg-red-400/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400/80" />
                  <div className="w-3 h-3 rounded-full bg-green-400/80" />
                  <div className="flex-1 mx-4">
                    <div className="h-5 rounded-md bg-background border border-border/50 flex items-center justify-center">
                      <span className="text-[10px] text-muted-foreground/60 font-medium tracking-wide">commentt.ai</span>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <img
                    src={theme === "dark" ? lightViewer : darkViewer}
                    alt="Commentt.ai document viewer"
                    className="w-full h-auto block"
                  />
                  <div
                    className="absolute bottom-6 right-6 w-[220px] sm:w-[280px] rounded-xl border border-border shadow-2xl overflow-hidden bg-card"
                    style={{ animation: 'landing-float 4s ease-in-out infinite' }}
                  >
                    <img
                      src={theme === "dark" ? lightComment : darkComment}
                      alt="Comment panel"
                      className="w-full h-auto block"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-border">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
            <div className="landing-animate mb-20 text-center lg:text-left">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-4">
                Key Features
              </p>
              <h2 className="font-display text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight leading-[1.1]">
                Everything you need.
                <br />
                <span className="text-muted-foreground font-light">Nothing you don't.</span>
              </h2>
            </div>

            <div className="space-y-20 sm:space-y-28">
              {featureRows.map(({ icon: Icon, title, desc, badges, accent }, i) => {
                const reverse = i % 2 === 1;
                return (
                  <div
                    key={title}
                    className={`landing-animate grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center`}
                  >
                    <div className={`text-center lg:text-left ${reverse ? "lg:order-2" : ""}`}>
                      <div className="flex items-center gap-3 mb-5 justify-center lg:justify-start">
                        <Icon className="h-10 w-10 text-primary" strokeWidth={1} />
                      </div>
                      <h3 className="font-display text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight leading-[1.15] mb-5">
                        {title}
                      </h3>
                      <p className="text-lg text-muted-foreground leading-relaxed mb-5 max-w-lg mx-auto lg:mx-0">
                        {desc}
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                        {badges.map(({ icon: BadgeIcon, label: badgeLabel }) => (
                          <span
                            key={badgeLabel}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background border border-border shadow-sm text-sm font-semibold text-foreground"
                          >
                            <BadgeIcon className="w-3.5 h-3.5 shrink-0 text-primary" strokeWidth={1} />
                            {badgeLabel}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className={`${reverse ? "lg:order-1" : ""}`}>
                      <PhoneFrame accent={accent} image={phoneImages[i % phoneImages.length]} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="hidden border-t border-border bg-muted/30">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
            <div className="landing-animate mb-16">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-4">
                Why teams switch
              </p>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight leading-[1.1]">
                Current tools
                <br />
                <span className="text-muted-foreground font-light">aren't built for this.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {competitors.map(({ tool, pain }, i) => (
                <div
                  key={tool}
                  className={`landing-animate landing-animate-delay-${i + 1} landing-card-hover bg-card rounded-2xl p-8 border border-border flex flex-col gap-3`}
                >
                  <span className="text-2xl font-bold text-foreground">{tool}</span>
                  <p className="text-base text-muted-foreground leading-relaxed">{pain}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="hidden border-t border-border">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
            <div className="landing-animate mb-16">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-4">
                How it works
              </p>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight leading-[1.1]">
                Three steps.
                <br />
                <span className="text-muted-foreground font-light">That's it.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {steps.map(({ step, icon: Icon, title, desc }, i) => (
                <div
                  key={step}
                  className={`landing-animate landing-animate-delay-${i + 1} landing-card-hover bg-card rounded-2xl p-8 sm:p-10 border border-border flex flex-col gap-4 relative overflow-hidden`}
                >
                  <span className="landing-step-number absolute top-4 right-6 select-none">{step}</span>
                  <div className="relative z-10 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <h3 className="relative z-10 text-xl font-bold text-foreground">{title}</h3>
                  <p className="relative z-10 text-base text-muted-foreground leading-relaxed">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="hidden border-t border-border bg-muted/30">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
            <div className="landing-animate text-center mb-4">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-4">
                Pricing
              </p>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight leading-[1.1] mb-3">
                Simple, flat pricing.
              </h2>
              <p className="text-xl text-muted-foreground font-light">
                No per-user fees. Ever.
              </p>
            </div>
            <p className="landing-animate landing-animate-delay-1 text-center text-sm text-muted-foreground/70 mb-14">
              Currently free during early access. Paid plans coming soon.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-start">
              {pricingPlans.map(({ plan, price, period, features: planFeatures, cta, href, highlight }, i) => (
                <div
                  key={plan}
                  className={`landing-animate landing-animate-delay-${i + 1} landing-card-hover rounded-2xl p-8 sm:p-10 flex flex-col gap-5 border ${
                    highlight
                      ? "landing-pricing-highlight bg-card border-primary/30 shadow-lg shadow-primary/5"
                      : "bg-card border-border"
                  }`}
                >
                  {highlight && (
                    <span className="text-xs font-bold uppercase tracking-widest text-primary">
                      Most Popular
                    </span>
                  )}
                  <div>
                    <span className="text-lg font-bold text-foreground">{plan}</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-extrabold text-foreground tracking-tight">{price}</span>
                    <span className="text-base text-muted-foreground font-light">{period}</span>
                  </div>
                  <ul className="flex flex-col gap-3 flex-1">
                    {planFeatures.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-base text-muted-foreground">
                        <Check className="h-4 w-4 mt-1 shrink-0 text-primary" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    asChild
                    variant={highlight ? "default" : "outline"}
                    size="lg"
                    className={`h-11 text-sm font-semibold w-full mt-3 rounded-xl ${highlight ? "landing-cta-glow" : ""}`}
                  >
                    <a href={href}>{cta}</a>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="hidden border-t border-border">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
            <div className="landing-animate mb-16">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary mb-4">
                Comparison
              </p>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight leading-[1.1]">
                See how we
                <br />
                <span className="text-muted-foreground font-light">stack up.</span>
              </h2>
            </div>
            <div className="landing-animate landing-animate-delay-1 border border-border rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-card border-b border-border">
                      <th className="text-left p-5 text-base font-bold text-foreground min-w-[180px]">Feature</th>
                      <th className="text-center p-5 min-w-[120px]">
                        <span className="text-base font-bold text-primary">Commentt.ai</span>
                      </th>
                      <th className="text-center p-5 text-base font-medium text-muted-foreground min-w-[110px]">Ziflow</th>
                      <th className="text-center p-5 text-base font-medium text-muted-foreground min-w-[110px]">GoVisually</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map(({ feature, commentt, ziflow, govisually }, i) => (
                      <tr key={feature} className={`border-b border-border last:border-0 ${i % 2 === 0 ? "bg-background" : "bg-card"}`}>
                        <td className="p-5 text-base font-medium text-foreground">{feature}</td>
                        <td className="p-5 text-center bg-primary/[0.03]">
                          {commentt === true && <Check className="h-5 w-5 mx-auto text-primary" />}
                        </td>
                        <td className="p-5 text-center">
                          {ziflow === true && <Check className="h-5 w-5 mx-auto text-muted-foreground/50" />}
                          {ziflow === false && <X className="h-5 w-5 mx-auto text-muted-foreground/25" />}
                          {ziflow === "partial" && (
                            <span className="text-xs text-muted-foreground/60">2-user cap</span>
                          )}
                        </td>
                        <td className="p-5 text-center">
                          {govisually === true && <Check className="h-5 w-5 mx-auto text-muted-foreground/50" />}
                          {govisually === false && <X className="h-5 w-5 mx-auto text-muted-foreground/25" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-border landing-footer-cta-bg">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
            <div className="landing-animate text-center max-w-3xl mx-auto">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground tracking-tight leading-[1.1] mb-6">
                Ditch complexity.
                <br />
                <span className="landing-hero-gradient">Review PDFs like</span>
                <br />
                you review Figma.
              </h2>
              <p className="text-xl text-muted-foreground font-light mb-10">
                Start free &mdash; no credit card required.
              </p>
              <Button asChild size="lg" className="h-14 px-10 text-lg font-semibold landing-cta-glow rounded-xl">
                <a href="/signup">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-sm text-muted-foreground">
            &copy; 2026 Commentt.ai
          </p>
        </div>
      </footer>
    </div>
  );
}
